<template>
  <div
    class="embedded-player-wrapper"
    :style="transforms"
    :class="{
      'embedded-player-wrapper--has-poster': !played,
      'embedded-player-wrapper--notes': canvasName === 'notes',
    }"
  >
    <NebulaBadge
      v-if="this.duration && this.showDuration"
      class="nebula-card__duration embedded-player-wrapper__duration"
      type="duration"
    >
      {{ duration }}
    </NebulaBadge>
    <EmbeddedPlayer
      ref="embeddedPlayer"
      :base-u-r-l="domains.deDomain"
      :video="playerVideo"
      :init-player-on-load="mode === 'EDIT'"
      :on-play-video-click="play"
      :start-time="startTime"
      @firstplay="onFirstPlay"
      @playing="onPlay"
      @player-initialized="onPlayerInitialized"
    >
      <template
        v-if="useCirclePlayIcon"
        #play-button-icon
      >
        <div
          class="embedded-player-wrapper__circle-play-icon"
          :class="useThemeablePlayIcon
            && `embedded-player-wrapper__circle-play-icon--${playButtonColorVariation}`"
        >
          <NebulaIcon symbol-id="circle-play--filled" />
        </div>
      </template>
      <template
        v-else-if="useMysteryPlayButton"
        #play-button-icon
      >
        <MysteryPlayButton
          class="embedded-player-wrapper__mystery-play-button"
          tag-name="span"
        />
      </template>
    </EmbeddedPlayer>
  </div>
</template>

<script>
import { get } from 'lodash-es';
import { EmbeddedPlayer } from '@discoveryedu/embedded-player';
import { MysteryPlayButton } from '@discoveryedu/mystery-components';
import { NebulaBadge, NebulaIcon } from '@discoveryedu/nebula-components';
import { mapState, mapActions } from 'pinia';
import { themes } from '@/lib/constants/themes.json';
import { bus } from '@/lib/eventBus';
import * as types from '@/lib/constants/store';
import { secondsToDuration } from '@/lib/utils';
import {
  useEditorStore,
  useTrackingStore,
} from '@/stores';

export default {
  name: 'VideoPlayer',
  components: {
    EmbeddedPlayer,
    MysteryPlayButton,
    NebulaBadge,
    NebulaIcon,
  },
  props: {
    autoplay: Boolean,
    loop: {
      type: Boolean,
      default: false,
    },
    moduleTheme: {
      type: Object,
      default: null,
    },
    video: {
      type: Object,
      required: true,
    },
    scale: {
      default: 1,
      type: Number,
    },
    themeName: {
      type: String,
      default: 'default',
    },
    startTime: {
      type: Number,
      default: null,
    },
    stopTime: {
      type: Number,
      default: null,
    },
    mode: {
      type: String,
      default: null,
    },
    assetId: {
      type: String,
      default: null,
    },
    canvasName: {
      type: String,
      default: null,
    },
  },
  emits: [
    'pause',
    'ended',
    'play',
  ],
  data() {
    return {
      playerCommandQueue: [],
      playerReady: false,
      playerVideo: this.video,
      videoStopped: false,
      showDuration: true,
      played: false,
    };
  },
  mounted() {
    this.$nextTick(this.onAutoPlayChanged);
    this.setPlayerControls();
  },
  beforeUnmount() {
    const player = this.getPlayer();
    if (player) {
      player.off('pause');
      player.off('ended');
      player.off('canplay');
    }
  },
  computed: {
    ...mapState(useEditorStore, [
      'editorIsFullscreen',
      'domains',
      'videoDurations',
    ]),
    ...mapState(useTrackingStore, [
      'videoPlays',
      'progressTracking',
    ]),
    duration() {
      const videoDuration = this.videoDurations[this.video?.id];
      if (videoDuration) return videoDuration.duration;
      const duration = this.video.duration || this.video.duration_in_seconds;
      return duration ? secondsToDuration(duration) : null;
    },
    theme() {
      return themes.find((theme) => theme.id === this.themeName);
    },
    playButtonColorVariation() {
      /*
        Make sure that the play button color variation in the
        module exists in the selected theme. If not, default to the first.
      */
      const defaultVariation = 1;
      const variation = this.moduleTheme?.variation || defaultVariation;
      const hasPlayButtonColor = !!(this.theme?.playButtonColors || [])
        .find((c) => c.id === variation);
      return hasPlayButtonColor ? variation : defaultVariation;
    },
    transforms() {
      // If the editor is scaled, scrubbing won't work correctly.
      return {
        transform: `scale(${1 / this.scale})`,
        width: `${this.scale * 100}%`,
        height: `${this.scale * 100}%`,
      };
    },
    useMysteryPlayButton() {
      return this.themeName === 'mystery-science' && this.$route?.name !== 'print-preview';
    },
    useCirclePlayIcon() {
      return [
        'k-2-science-earth',
        'k-2-science-life',
        'k-2-science-physical',
        'sesame-workshop',
        'the-electric-company',
        'teach',
        'need-to-know-social-studies',
        'scc',
        'timepod-adventures',
      ].includes(this.themeName) && this.$route?.name !== 'print-preview';
    },
    useThemeablePlayIcon() {
      return [
        'the-electric-company',
        'teach',
        'need-to-know-social-studies',
        'timepod-adventures',
      ].includes(this.themeName);
    },
  },
  watch: {
    autoplay() {
      this.onAutoPlayChanged();
    },
    editorIsFullscreen(value) {
      if (value) {
        this.onAutoPlayChanged();
      } else if (!this.paused()) { // pause video when exiting fullscreen
        this.pause();
      }
    },
    mode() {
      if (this.mode === 'EDIT') {
        this.tryInitPlayer();
      }
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.SET_DURATION,
    ]),
    ...mapActions(useTrackingStore, [
      types.STORE_VIDEO_PLAY_PERCENTAGE,
    ]),
    onPlayerInitialized(player) {
      if (player) {
        player.responsive(true);
        player.breakpoints({
          xsmall: 264,
          small: 375,
        });
        player.on('canplay', () => {
          this.playerReady = true;
          this.setVideoDurationInAppStore(player);
        });
      }
    },
    setPlayerControls() {
      if (this.mode === 'VIEW' && !this.autoplay) {
        const playerRef = this.$refs?.embeddedPlayer?.$refs?.embeddedVideoPlayer?.$refs?.player;
        if (playerRef) {
          playerRef.controls = false;
          playerRef.tabIndex = -1;
        }
      }
    },
    setVideoDurationInAppStore(player) {
      if (player.duration()) {
        this[types.SET_DURATION](
          {
            id: this.video.id,
            duration: secondsToDuration(player.duration()),
          },
        );
      }
    },
    onAutoPlayChanged() {
      if (this.autoplay && this.editorIsFullscreen) {
        // Ensure video plays from the designated start time.
        this.play(this.startTime);
      }
    },
    pause() {
      this.enqueuePlayerCommand((player) => {
        // Avoid possible console error if internal player_ is null
        if (get(player, 'player_')) {
          player.pause();
        }
      });
    },
    reset() {
      // Reset the video player back to its default state (before play clicked)
      this.enqueuePlayerCommand((player, embedded) => {
        const embeddedPlayer = embedded;
        embeddedPlayer.fadeUI = false;
        embeddedPlayer.playingUI = false;
        // Reset the video at the startTime
        player.currentTime(this.startTime);
        this.videoStopped = false;
        this.showDuration = true;
        this.played = false;
      });
    },
    onFirstPlay() {
      const embeddedPlayer = this.getEmbeddedPlayer();
      const self = this;

      this.showDuration = false;

      if (embeddedPlayer) {
        const player = this.getPlayer();
        if (player) {
          player.on('pause', () => {
            this.$emit('pause');
          });
          player.on('ended', () => {
            if (self.loop) {
              self.play();
            }
            this.$emit('ended');
          });
          player.on('timeupdate', () => {
            bus.emit('video:timeupdate');
            if (this.stopTime !== null && !this.videoStopped) {
              const currentTime = Math.round(player.currentTime()) - 1;
              if (currentTime === this.stopTime) {
                player.pause();
                this.videoStopped = true;
                // Ensure video paused once during current play.
              }
            }

            if (this.assetId && this.progressTracking?.enabled && this.mode === 'VIEW') {
              const playedTime = player.currentTime();
              const duration = this.stopTime || player.duration();

              const perc = Math.round((playedTime / duration) * 100);
              const percMap = [100, 75, 50, 25, 0];
              for (let x = 0; x < percMap.length; x += 1) {
                // use the first match
                if (
                  perc >= percMap[x]
                  && (!this.videoPlays[this.assetId] || this.videoPlays[this.assetId] < percMap[x])
                ) {
                  this[types.STORE_VIDEO_PLAY_PERCENTAGE](this.assetId, percMap[x]);
                  break;
                }
              }
            }
          });
          if (this.mode === 'VIEW' && !this.autoplay) {
            player.controls(true);
          }
          // prevent focus from going outside of the video player
          // eslint-disable-next-line no-underscore-dangle
          player.tech_.el_.focus();
        }
      }

      this.onPlay();
    },
    onPlay() {
      this.$emit('play');
      this.showDuration = false;
      this.played = true;
    },
    paused() {
      const player = this.getPlayer();
      return player ? player.paused() : true;
    },
    play(fromTime) {
      /*
        Initialize the player the first time we play
        the video. Player initialization is deferred until
        this point so it doesn't freeze UI animations since
        it can be an expensive operation.
      */
      this.tryInitPlayer();
      this.enqueuePlayerCommand((player, embedded) => {
        const embeddedPlayer = embedded;
        embeddedPlayer.fadeUI = true;
        embeddedPlayer.playingUI = true;
        this.played = true;
        if (fromTime !== undefined) {
          player.currentTime(fromTime);
        }
        player.play();
      });
    },
    getEmbeddedPlayer() {
      return this.$refs?.embeddedPlayer?.$refs?.embeddedVideoPlayer;
    },
    getPlayer() {
      return this.getEmbeddedPlayer()?.player;
    },
    tryInitPlayer() {
      if (this.getPlayer()) {
        return;
      }
      const embeddedPlayer = this.getEmbeddedPlayer();
      if (!embeddedPlayer) return;
      embeddedPlayer.initPlayer();
      const player = this.getPlayer();
      if (player?.readyState?.() === 4) { // HAVE_ENOUGH_DATA
        /*
          If the player is ready to play, then run all
          player commands currently in the queue.
        */
        this.playerReady = true;
        this.runPlayerCommands();
        this.setVideoDurationInAppStore(player);
      } else {
        /*
          Otherwise wait for the 'canplay' event before
          attempting to run any commands on the player.
        */
        player.on('canplay', () => {
          this.playerReady = true;
          this.runPlayerCommands();
          this.setVideoDurationInAppStore(player);
        });
      }
    },
    runPlayerCommands() {
      /*
        If the player is ready to play, then run
        all the player commands currently in the queue.
      */
      if (this.playerReady) {
        const player = this.getPlayer();
        const embeddedPlayer = this.getEmbeddedPlayer();
        while (this.playerCommandQueue.length) {
          const commandFunc = this.playerCommandQueue.shift();
          commandFunc(player, embeddedPlayer);
        }
      }
    },
    /**
     * Commands on the player should be queued here so they
     * can run only when the player is ready. Functions on the
     * player should not be called directly without being queued.
     */
    enqueuePlayerCommand(commandFunc) {
      this.playerCommandQueue.push(commandFunc);
      this.runPlayerCommands();
    },
  },
};
</script>

<style lang="stylus">
.embedded-player-wrapper {
  height: 100%;
  position: relative;
  transform-origin: top left;

  &--has-poster {
    .video-player {
      display: none;
    }
  }

  .video-player {
    height: 100%;

    &__thumbnail {
      height: 100%;
    }

    .video-js {
      .vjs-big-play-button {
        display: none;
      }

      &.vjs-layout-tiny,
      &.vjs-layout-x-small,
      &.vjs-layout-small {
        .vjs-mute-control {
          display: block;
        }

        .vjs-volume-panel {
          width: 4em;
        }
      }
    }

    &__trigger--play {
      height: 120px;
      left: calc(50% - 60px);
      top: calc(50% - 60px);
      width: 120px;
    }
  }

  &--notes {
    .video-player__trigger--play {
      height: 80px;
      left: calc(50% - 40px);
      top: calc(50% - 40px);
      width: 80px;
    }

    .embedded-player-wrapper__circle-play-icon {
      height: 80px;
      left: calc(50% - 40px);
      top: calc(50% - 40px);
      width: 80px;
    }
  }

  .video-player__details {
    display: none;
  }

  .video-js, .vjs-tech {
    height: 100%;
    width: 100%; // force video to width of module
  }

  .vjs-text-track-display {
    z-index: 1;
  }

  .video-js .vjs-control-bar {
    transform: translateZ(0);
    z-index: 1;
  }

  .vjs-modal-dialog select {
    color: $comet-color-black;
  }

  &__duration {
    z-index: 21; // above the video thumbnail
  }

  themed-play-button(size) {
    $size-half = size / 2;
    height: size;
    left: "calc(50% - %s)" % $size-half;
    position: absolute;
    top: "calc(50% - %s)" % $size-half;
    width: size;
  }

  &__mystery-play-button {
    themed-play-button(94px);
  }

  &__circle-play-icon {
    themed-play-button(92px);
    border-radius: 50%;
    box-shadow: 0px 12px 10px rgba(0, 0, 0, 0.4);

    svg.nebula-icon {
      // Overlap icon on background a few pixels to avoid white ring around border
      $circle-play-icon-overlap = 2px;
      $circle-play-icon-offset = -($circle-play-icon-overlap / 2);
      height: "calc(100% + %s)" % $circle-play-icon-overlap;
      left: $circle-play-icon-offset;
      position: absolute;
      top: $circle-play-icon-offset;
      transition: $nebula-transition-default;
      width: "calc(100% + %s)" % $circle-play-icon-overlap;
    }
  }

  html[dir="rtl"] & {
    transform-origin: top right;
  }
}
</style>
